import _ from 'lodash';

const validateImageUrl = (url, required) => new Promise((resolve, reject) => {
    if (!required || _.trim(url).length > 0) {
        var img = new Image();
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
    }
});

const getValidateImageUrl = (value, required = false) => {
    let isValid = false;
    (async () => await validateImageUrl(value, required))()
        .then(() => isValid = true);
    return {
        value: isValid,
        message: '{name} is unreachable or does not exist.'
    };
}

const validateYesNo = (value, required) => {
    let v = _.trim(value).toLowerCase();
    return (!required || v.length > 0) || v == "yes" || v == 'no';
}

const getYesNoValidation = (value, required = true) => {
    let isValid = validateYesNo(value, required);
    return {
        value: isValid,
        message: '{name} is required and must be either "Yes" or "No"'
    };
}

const getComplexPricingLogicValidation = (value) => {
    let v = _.trim(value);
    if (v.length > 0) {
        try {
            let hasF = v.includes('$f');
            if (!hasF) {
                const $m = 15;
                const $mc = 20 / $m;
                const $c = 12;
                const $p = 1;
                const logic = new Function('$m', '$mc', '$c', '$p', v);
                logic($m, $mc, $c, $p);
            }
            return {
                value: true,
                warning: hasF,
                message: "Unable to validate due to use of $f, $fa, or $fs variables."
            };
        }
        catch (ex) {
            return {
                value: false,
                message: "Complex Pricing Logic failed validation: " + ex.message
            };
        }
    }
    return {
        value: true
    }
}


const ValidateRow = (row) => {
    let errors = {};
    let warnings = {};
    for (let key in row) {
        if (!key.startsWith('filter:')) {
            const col = HEADERS[key];
            const { name, validation } = col;
            if (validation && typeof validation === 'object') {
                const { maxLength, minLength, mustMatch } = validation;
                if (maxLength) {
                    if (row[key].length > maxLength) {
                        errors[key] = `${name} cannot be longer than ${maxLength} characters in length.`;
                    }
                }
                if (minLength) {
                    if (row[key].length < minLength) {
                        errors[key] = `${name} cannot be shorter than ${minLength} characters in length.`;
                    }
                }
                if (mustMatch) {
                    for (var reg in mustMatch.value) {
                        if (!mustMatch.value[reg].test(row[key])) {
                            errors[key] = mustMatch.message.replace("{name}", name);
                        }
                    }
                }
            } else if (validation && typeof validation === 'function') {
                let result = validation(row[key]);
                if (!result.value) {
                    errors[key] = result.message;
                } else if (result.value && result.warning) {
                    warnings[key] = result.message;
                }
            }
        }
        return { errors, warnings };
    }
}

const HEADERS = {
    ProductName: {
        name: 'Product Name',
        validation: {
            maxLength: 150,
            minLength: 3,
            mustMatch: {
                value: [/[a-z]/gi],
                message: "{name} cannot be only numeric digits."
            }
        }
    },
    DisplayName: {
        name: "Display Name"
    },
    CloneLayersFrom: {
        name: "Clone Layers From"
    },
    Brand: {
        name: 'Brand',
        validation: {
            maxLength: 150,
            minLength: 2
        }
    },
    Manufacturer: {
        name: 'Manufacturer'
    },
    Sku: {
        name: 'Sku',
        validation: {
            maxLength: 32,
            minLength: 2
        }
    },
    Model: {
        name: 'Model',
        validation: {
            maxLength: 64
        }
    },
    FeatureGroup: {
        name: 'Feature Group',
        validation: {
            maxLength: 100,
            minLength: 2
        }
    },
    FeatureCategory: {
        name: 'Feature Category',
        validation: {
            maxLength: 100
        }
    },
    RetailPrice: {
        name: 'Retail Price',
        validation: {
            minLength: 1,
            mustMatch: {
                value: [/[0-9\.\,\$]/g],
                message: 'Please enter the retail price in ###,###.## format'
            }
        }
    },
    Cost: {
        name: 'Cost',
        validation: {
            mustMatch: {
                value: [/[0-9\.\,\$]/g],
                message: 'Please enter the cost in ###,###.## format'
            }
        }
    },
    Description: {
        name: 'Description',
        validation: {
            maxLength: 1024
        }
    },
    SubDescription: {
        name: 'Sub Description',
        validation: {
            maxLength: 255
        }
    },
    ThumbnailUrl: {
        name: 'Thumbnail URL',
        validation: (value) => getValidateImageUrl(value, true)
    },
    MeasurementUnit: {
        name: 'Measurement Unit',
        validation: {
            maxLength: 64,
            minLength: 2
        }
    },
    DefaultQuantity: {
        name: 'Default Quantity',
        validation: {
            mustMatch: {
                value: [/[0-9\,]/g],
                message: 'Please enter the default quantity in ###,### format'
            }
        }
    },
    EnabledForKV: {
        name: 'EnabledForKV',
        validation: getYesNoValidation
    },
    EnabledForBV: {
        name: 'Enabled For BV',
        validation: getYesNoValidation
    },
    HideUnitLabel: {
        name: 'Hide Unit Label',
        validation: getYesNoValidation
    },
    NotForSale: {
        name: 'Not For Sale',
        validation: getYesNoValidation
    },
    EmployeesOnly: {
        name: 'EmployeesOnly',
        validation: getYesNoValidation
    },
    HideQuantity: {
        name: 'HideQuantity',
        validation: getYesNoValidation
    },
    Color1: {
        name: 'Color 1'
    },
    Color2: {
        name: 'Color 2'
    },
    Color3: {
        name: 'Color 3'
    },
    Material1: {
        name: 'Material 1'
    },
    Material2: {
        name: 'Material 2'
    },
    Material3: {
        name: 'Material 3'
    },
    GalleryEnabled: {
        name: 'Gallery Enabled',
        validation: getYesNoValidation
    },
    GalleryImage1Url: {
        name: 'Gallery Image 1 - Url',
        validation: getValidateImageUrl
    },
    GalleryImage1Featured: {
        name: 'Gallery Image 1 - Featured',
        validation: getYesNoValidation
    },
    GalleryImage1Default: {
        name: 'Gallery Image 1 - Default',
        validation: getYesNoValidation
    },
    GalleryImage2Url: {
        name: 'Gallery Image 2 - Url',
        validation: getValidateImageUrl
    },
    GalleryImage2Featured: {
        name: 'Gallery Image 2 - Featured',
        validation: getYesNoValidation
    },
    GalleryImage2Default: {
        name: 'Gallery Image 2 - Default',
        validation: getYesNoValidation
    },
    GalleryImage3Url: {
        name: 'Gallery Image 3 - Url',
        validation: getValidateImageUrl
    },
    GalleryImage3Featured: {
        name: 'Gallery Image 3 - Featured',
        validation: getYesNoValidation
    },
    GalleryImage3Default: {
        name: 'Gallery Image 3 - Default',
        validation: getYesNoValidation
    },
    ComplexPricingLogic: {
        name: 'Complex Pricing Logic',
        validation: getComplexPricingLogicValidation
    },
    RelatedFeatureSkus: {
        name: 'Related Feature Skus'
    }
}

export default HEADERS;
export { HEADERS, ValidateRow };